import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, Check, User } from "lucide-react";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalDateNavigator from "../../../global/GlobalDateNavigator";
import GlobalLabelFilters from "../../../global/GlobalLabelFilters";
import GlobalDataTable, { TableCell, TableRow } from "../../../global/GlobalDataTable";
import GlobalDirectoryShell from "../../../global/directory/GlobalDirectoryShell";
import GlobalDirectoryFiltersSection from "../../../global/directory/GlobalDirectoryFiltersSection";
import GlobalDirectoryColumnPicker from "../../../global/directory/GlobalDirectoryColumnPicker";
import { GlobalDirectoryExportMenu } from "../../../global/directory";
import GlobalDirectoryPaginationSection from "../../../global/directory/GlobalDirectoryPaginationSection";
import {
  formatDateLabel,
  partsFromIso,
  toIsoDate,
  todayParts,
} from "../../../global/dateNavUtils";
import {
  PresenceBarChart,
  StatusDonutChart,
  WorkedMinutesSparkline,
} from "../components/report/AttendanceReportCharts";
import {
  CHART_RANGE_ITEMS,
  aggregatePresenceSeries,
  aggregateStatusBreakdown,
  aggregateWorkedSeries,
  chartRowsForRange,
  rangeNeedsYearData,
} from "../utils/attendanceReportChartUtils";
import { fetchAttendanceUsers, fetchAttendanceReport } from "../api/attendanceOperationsApi";
import "../styles/attendance.css";
import "../styles/attendance-report.css";

const boot = todayParts();
const PAGE_SIZE = 15;
const COLUMN_VISIBILITY_KEY = "girinestham.attendance.report.columnVisibility";

const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const LEDGER_COLUMN_DEFS = [
  { id: "user_name", label: "User" },
  { id: "attendance_date", label: "Date" },
  { id: "first_in", label: "In" },
  { id: "last_out", label: "Out" },
  { id: "worked_minutes", label: "Worked" },
  { id: "location_label", label: "Location" },
  { id: "shift_name", label: "Shift" },
  { id: "status", label: "Status" },
  { id: "present", label: "Present" },
  { id: "absent", label: "Absent" },
  { id: "late", label: "Late" },
  { id: "leave", label: "Leave" },
];

const EMPTY_DIRECTORY_FILTERS = { search: "", column: "", value: "" };
const DEFAULT_CHART_RANGES = { presence: "daily", status: "daily", worked: "daily" };

function defaultColumnVisibility() {
  return Object.fromEntries(LEDGER_COLUMN_DEFS.map((c) => [c.id, true]));
}

function loadColumnVisibility() {
  const defaults = defaultColumnVisibility();
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return { ...defaults, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return defaults;
}

function rowFlagActive(row, columnId) {
  const status = String(row.status ?? "").toUpperCase();
  if (columnId === "present") return status === "PRESENT";
  if (columnId === "absent") return status === "ABSENT";
  if (columnId === "late") return status === "LATE" || row.is_late;
  if (columnId === "leave") return status === "LEAVE" || row.is_leave;
  return false;
}

function formatTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatWorked(mins) {
  const m = Number(mins ?? 0);
  if (m <= 0) return "0m";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

function statusTone(status) {
  const s = String(status ?? "").toUpperCase();
  if (s === "PRESENT") return "present";
  if (s === "ABSENT") return "absent";
  if (s === "LATE") return "late";
  if (s === "LEAVE") return "leave";
  return "neutral";
}

function StatusBadge({ status }) {
  const tone = statusTone(status);
  return <span className={`att-rpt-status att-rpt-status--${tone}`}>{status || "—"}</span>;
}

function FlagCell({ active }) {
  return (
    <span className={`att-rpt-flag${active ? " att-rpt-flag--on" : ""}`} aria-hidden={!active}>
      {active ? <Check size={14} strokeWidth={2.5} /> : "—"}
    </span>
  );
}

function applyDirectoryFilters(rows, filters) {
  let next = rows;
  const search = String(filters.search ?? "").trim().toLowerCase();
  const column = String(filters.column ?? "").trim();
  const value = String(filters.value ?? "").trim();

  if (search) {
    next = next.filter((row) => {
      const hay = [
        row.user_name,
        row.location_label,
        row.shift_name,
        row.status,
        row.attendance_date,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(search);
    });
  }

  if (column && value) {
    next = next.filter((row) => {
      switch (column) {
        case "user_name":
          return String(row.user_id) === value || String(row.user_name ?? "") === value;
        case "attendance_date":
          return String(row.attendance_date ?? "") === value;
        case "first_in":
          return formatTime(row.first_in) === value;
        case "last_out":
          return formatTime(row.last_out) === value;
        case "worked_minutes":
          return formatWorked(row.worked_minutes) === value;
        case "location_label":
          return String(row.location_label ?? "") === value;
        case "shift_name":
          return String(row.shift_name ?? "") === value;
        case "status":
          return String(row.status ?? "").toUpperCase() === value;
        case "present":
        case "absent":
        case "late":
        case "leave":
          return rowFlagActive(row, column) === (value === "yes");
        default:
          return true;
      }
    });
  }

  return next;
}

function renderLedgerCell(columnId, row) {
  const status = String(row.status ?? "").toUpperCase();
  switch (columnId) {
    case "user_name":
      return <span className="att-rpt-table__user">{row.user_name || "—"}</span>;
    case "attendance_date":
      return row.attendance_date || "—";
    case "first_in":
      return formatTime(row.first_in);
    case "last_out":
      return formatTime(row.last_out);
    case "worked_minutes":
      return formatWorked(row.worked_minutes);
    case "location_label":
      return row.location_label || "—";
    case "shift_name":
      return row.shift_name || "—";
    case "status":
      return <StatusBadge status={row.status} />;
    case "present":
      return <FlagCell active={status === "PRESENT"} />;
    case "absent":
      return <FlagCell active={status === "ABSENT"} />;
    case "late":
      return <FlagCell active={status === "LATE" || row.is_late} />;
    case "leave":
      return <FlagCell active={status === "LEAVE" || row.is_leave} />;
    default:
      return "—";
  }
}

function ledgerCellExportValue(columnId, row) {
  const status = String(row.status ?? "").toUpperCase();
  switch (columnId) {
    case "user_name":
      return row.user_name || "";
    case "attendance_date":
      return row.attendance_date || "";
    case "first_in":
      return formatTime(row.first_in) === "—" ? "" : formatTime(row.first_in);
    case "last_out":
      return formatTime(row.last_out) === "—" ? "" : formatTime(row.last_out);
    case "worked_minutes":
      return formatWorked(row.worked_minutes);
    case "location_label":
      return row.location_label || "";
    case "shift_name":
      return row.shift_name || "";
    case "status":
      return status || "";
    case "present":
    case "absent":
    case "late":
    case "leave":
      return rowFlagActive(row, columnId) ? "Yes" : "No";
    default:
      return "";
  }
}

function uniqueOptions(values) {
  return [...new Set(values.filter(Boolean))].map((v) => ({ value: v, label: v }));
}

export default function AttendanceReportPage() {
  const [year, setYear] = useState(boot.year);
  const [month, setMonth] = useState(boot.month);
  const [day, setDay] = useState(boot.day);
  const [directoryFilters, setDirectoryFilters] = useState(EMPTY_DIRECTORY_FILTERS);
  const [columnVisibility, setColumnVisibility] = useState(loadColumnVisibility);
  const [chartRanges, setChartRanges] = useState(DEFAULT_CHART_RANGES);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [monthReport, setMonthReport] = useState(null);
  const [yearReport, setYearReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearLoading, setYearLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const isoDate = toIsoDate(year, month, day);
  const periodLabel = formatDateLabel(isoDate);

  useEffect(() => {
    fetchAttendanceUsers()
      .then(setUsers)
      .catch((err) => setErrorMsg(err.message));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const [dayData, monthData] = await Promise.all([
        fetchAttendanceReport({ year, month, day }),
        fetchAttendanceReport({ year, month }),
      ]);
      setReport(dayData);
      setMonthReport(monthData);
    } catch (err) {
      setErrorMsg(err.message);
      setReport(null);
      setMonthReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, day]);

  useEffect(() => {
    load();
  }, [load]);

  const needsYearData = useMemo(
    () => Object.values(chartRanges).some(rangeNeedsYearData),
    [chartRanges],
  );

  useEffect(() => {
    if (!needsYearData) {
      setYearReport(null);
      setYearLoading(false);
      return;
    }
    let cancelled = false;
    setYearLoading(true);
    fetchAttendanceReport({ year })
      .then((data) => {
        if (!cancelled) setYearReport(data);
      })
      .catch(() => {
        if (!cancelled) setYearReport(null);
      })
      .finally(() => {
        if (!cancelled) setYearLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, needsYearData]);

  const setChartRange = (chartKey, range) => {
    setChartRanges((prev) => ({ ...prev, [chartKey]: range }));
  };

  const handleDateChange = (iso) => {
    const parts = partsFromIso(iso);
    if (!parts) return;
    setYear(parts.year);
    setMonth(parts.month);
    setDay(parts.day);
    setDirectoryFilters(EMPTY_DIRECTORY_FILTERS);
    setPage(1);
  };

  const clearDirectoryFilters = () => {
    setDirectoryFilters(EMPTY_DIRECTORY_FILTERS);
    setPage(1);
  };

  const valueFilterOptions = useMemo(() => {
    const column = directoryFilters.column;
    const daily = report?.daily ?? [];

    if (!column) return [];

    if (column === "user_name") {
      const ids = new Set(daily.map((r) => String(r.user_id)));
      return users
        .filter((u) => ids.has(String(u.id)))
        .map((u) => ({ value: String(u.id), label: u.name }));
    }

    if (column === "status") {
      const statuses = [...new Set(daily.map((r) => String(r.status ?? "").toUpperCase()).filter(Boolean))];
      return statuses.map((s) => ({ value: s, label: s.replace(/_/g, " ") }));
    }

    if (column === "present" || column === "absent" || column === "late" || column === "leave") {
      return YES_NO_OPTIONS;
    }

    if (column === "attendance_date") {
      return uniqueOptions(daily.map((r) => String(r.attendance_date ?? "").trim()));
    }

    if (column === "location_label") {
      return uniqueOptions(daily.map((r) => String(r.location_label ?? "").trim()));
    }

    if (column === "shift_name") {
      return uniqueOptions(daily.map((r) => String(r.shift_name ?? "").trim()));
    }

    if (column === "first_in") {
      return uniqueOptions(daily.map((r) => formatTime(r.first_in)).filter((t) => t !== "—"));
    }

    if (column === "last_out") {
      return uniqueOptions(daily.map((r) => formatTime(r.last_out)).filter((t) => t !== "—"));
    }

    if (column === "worked_minutes") {
      return uniqueOptions(daily.map((r) => formatWorked(r.worked_minutes)));
    }

    return [];
  }, [directoryFilters.column, report?.daily, users]);

  const visibleColumns = useMemo(
    () => LEDGER_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const tableHeaders = useMemo(
    () => visibleColumns.map((c) => ({
      key: c.id,
      label: c.label,
      className: ["present", "absent", "late", "leave"].includes(c.id) ? "att-rpt-table__flag" : undefined,
    })),
    [visibleColumns],
  );

  const filteredDaily = useMemo(
    () => applyDirectoryFilters(report?.daily ?? [], directoryFilters),
    [report?.daily, directoryFilters],
  );

  const getLedgerExportData = useCallback(() => {
    const headers = visibleColumns.map((c) => c.label);
    const rows = filteredDaily.map((row) =>
      visibleColumns.map((col) => ledgerCellExportValue(col.id, row)),
    );
    return { headers, rows };
  }, [filteredDaily, visibleColumns]);

  const filteredMonthDaily = useMemo(
    () => applyDirectoryFilters(monthReport?.daily ?? [], directoryFilters),
    [monthReport?.daily, directoryFilters],
  );

  const filteredYearDaily = useMemo(
    () => applyDirectoryFilters(yearReport?.daily ?? [], directoryFilters),
    [yearReport?.daily, directoryFilters],
  );

  const chartCtx = useMemo(() => ({ year, month }), [year, month]);

  const presenceChart = useMemo(() => {
    const rows = chartRowsForRange(filteredMonthDaily, filteredYearDaily, chartRanges.presence);
    return aggregatePresenceSeries(rows, chartRanges.presence, chartCtx);
  }, [filteredMonthDaily, filteredYearDaily, chartRanges.presence, chartCtx]);

  const statusChart = useMemo(() => {
    const rows = chartRowsForRange(filteredMonthDaily, filteredYearDaily, chartRanges.status);
    return aggregateStatusBreakdown(rows, chartRanges.status, chartCtx);
  }, [filteredMonthDaily, filteredYearDaily, chartRanges.status, chartCtx]);

  const workedChart = useMemo(() => {
    const rows = chartRowsForRange(filteredMonthDaily, filteredYearDaily, chartRanges.worked);
    return aggregateWorkedSeries(rows, chartRanges.worked, chartCtx);
  }, [filteredMonthDaily, filteredYearDaily, chartRanges.worked, chartCtx]);

  const chartsLoading = loading || (needsYearData && yearLoading);

  useEffect(() => {
    setPage(1);
  }, [directoryFilters]);

  const totalRows = filteredDaily.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedDaily = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredDaily.slice(start, start + PAGE_SIZE);
  }, [filteredDaily, page]);

  const statusBreakdown = useMemo(() => {
    const breakdown = {};
    for (const row of filteredDaily) {
      const status = String(row.status ?? "UNKNOWN").toUpperCase();
      breakdown[status] = (breakdown[status] ?? 0) + 1;
    }
    return breakdown;
  }, [filteredDaily]);

  const headline = useMemo(() => {
    const b = statusBreakdown;
    return {
      present: Number(b.PRESENT ?? 0),
      absent: Number(b.ABSENT ?? 0),
      late: Number(b.LATE ?? 0),
      leave: Number(b.LEAVE ?? 0),
    };
  }, [statusBreakdown]);

  const filterSummary = useMemo(() => {
    const parts = [periodLabel];
    if (directoryFilters.search.trim()) parts.push(`search “${directoryFilters.search.trim()}”`);
    if (directoryFilters.column && directoryFilters.value) {
      const col = LEDGER_COLUMN_DEFS.find((c) => c.id === directoryFilters.column)?.label ?? directoryFilters.column;
      const opt = valueFilterOptions.find((o) => o.value === directoryFilters.value);
      parts.push(`${col}: ${opt?.label ?? directoryFilters.value}`);
    }
    return parts.join(" · ");
  }, [periodLabel, directoryFilters, valueFilterOptions]);

  return (
    <div className="att-shell att-rpt-page">
      <GlobalFeedback type="error" text={errorMsg} />

      <header className="att-rpt-hero" aria-label="Attendance analytics">
        <div className="att-rpt-hero__copy">
          <p className="att-rpt-hero__eyebrow">Analytics</p>
          <h1 className="att-rpt-hero__title">Attendance insights</h1>
          <p className="att-rpt-hero__sub">{filterSummary}</p>
        </div>
        <dl className="att-rpt-hero__kpis">
          <div>
            <dt>Present</dt>
            <dd>{loading ? "…" : headline.present}</dd>
          </div>
          <div>
            <dt>Absent</dt>
            <dd>{loading ? "…" : headline.absent}</dd>
          </div>
          <div>
            <dt>Late</dt>
            <dd>{loading ? "…" : headline.late}</dd>
          </div>
          <div>
            <dt>Leave</dt>
            <dd>{loading ? "…" : headline.leave}</dd>
          </div>
        </dl>
      </header>

      <div className="att-rpt-charts">
        <section className="att-rpt-chart-panel" aria-labelledby="att-rpt-bars-title">
          <header className="att-rpt-chart-panel__head">
            <div className="att-rpt-chart-panel__title">
              <BarChart3 size={16} aria-hidden />
              <div>
                <h2 id="att-rpt-bars-title">Daily presence</h2>
                <p>Present headcount</p>
              </div>
            </div>
            <GlobalLabelFilters
              className="att-rpt-chart-range"
              ariaLabel="Presence chart range"
              items={CHART_RANGE_ITEMS}
              activeKey={chartRanges.presence}
              onToggle={(key) => setChartRange("presence", key)}
            />
          </header>
          <div className="att-rpt-chart-panel__body att-rpt-chart-panel__body--fill">
            {chartsLoading ? (
              <div className="att-rpt-skeleton att-rpt-skeleton--chart" aria-busy="true" />
            ) : (
              <PresenceBarChart labels={presenceChart.labels} values={presenceChart.values} />
            )}
          </div>
        </section>

        <section className="att-rpt-chart-panel" aria-labelledby="att-rpt-mix-title">
          <header className="att-rpt-chart-panel__head">
            <div className="att-rpt-chart-panel__title">
              <div>
                <h2 id="att-rpt-mix-title">Status mix</h2>
                <p>Attendance breakdown</p>
              </div>
            </div>
            <GlobalLabelFilters
              className="att-rpt-chart-range"
              ariaLabel="Status chart range"
              items={CHART_RANGE_ITEMS}
              activeKey={chartRanges.status}
              onToggle={(key) => setChartRange("status", key)}
            />
          </header>
          <div className="att-rpt-chart-panel__body att-rpt-chart-panel__body--fill att-rpt-chart-panel__body--center">
            {chartsLoading ? (
              <div className="att-rpt-skeleton att-rpt-skeleton--chart" aria-busy="true" />
            ) : (
              <StatusDonutChart
                breakdown={statusChart}
                bucketMode={chartRanges.status !== "daily"}
              />
            )}
          </div>
        </section>

        <section className="att-rpt-chart-panel" aria-labelledby="att-rpt-spark-title">
          <header className="att-rpt-chart-panel__head">
            <div className="att-rpt-chart-panel__title">
              <div>
                <h2 id="att-rpt-spark-title">Worked time</h2>
                <p>Minutes trend</p>
              </div>
            </div>
            <GlobalLabelFilters
              className="att-rpt-chart-range"
              ariaLabel="Worked time chart range"
              items={CHART_RANGE_ITEMS}
              activeKey={chartRanges.worked}
              onToggle={(key) => setChartRange("worked", key)}
            />
          </header>
          <div className="att-rpt-chart-panel__body att-rpt-chart-panel__body--fill">
            {chartsLoading ? (
              <div className="att-rpt-skeleton att-rpt-skeleton--chart" aria-busy="true" />
            ) : (
              <WorkedMinutesSparkline
                labels={workedChart.labels}
                values={workedChart.values}
                latest={workedChart.latest}
              />
            )}
          </div>
        </section>
      </div>

      <section className="att-rpt-ledger" aria-labelledby="att-rpt-ledger-title">
        <header className="att-rpt-ledger__head">
          <div>
            <h2 id="att-rpt-ledger-title">Daily ledger</h2>
            <p>
              {loading
                ? "Loading records…"
                : `${totalRows} row${totalRows === 1 ? "" : "s"} · ${periodLabel}`}
            </p>
          </div>
        </header>

        <GlobalDirectoryShell
          className="gds-shell--compact att-rpt-directory"
          filters={(
            <GlobalDirectoryFiltersSection
              className="att-rpt-directory-filters"
              disabled={loading}
              searchLabel="Search"
              searchPlaceholder="User, location, status…"
              searchValue={directoryFilters.search}
              onSearchChange={(v) =>
                setDirectoryFilters((prev) => ({ ...prev, search: v }))
              }
              columnLabel="Column"
              columnValue={directoryFilters.column}
              onColumnChange={(v) =>
                setDirectoryFilters((prev) => ({ ...prev, column: v, value: "" }))
              }
              gridColumnDefs={LEDGER_COLUMN_DEFS}
              valueLabel="Value"
              valueValue={directoryFilters.value}
              onValueChange={(v) =>
                setDirectoryFilters((prev) => ({ ...prev, value: v }))
              }
              valueOptions={valueFilterOptions}
              valueSelectDisabled={!directoryFilters.column}
              onClear={clearDirectoryFilters}
              clearFilterLabel="Clear filter"
              secondaryStart={(
                <GlobalDirectoryColumnPicker
                  columnDefs={LEDGER_COLUMN_DEFS}
                  visibility={columnVisibility}
                  onVisibilityChange={setColumnVisibility}
                  storageKey={COLUMN_VISIBILITY_KEY}
                />
              )}
              secondaryEnd={(
                <div className="att-rpt-directory-toolbar__end">
                  <GlobalDateNavigator
                    value={isoDate}
                    onChange={handleDateChange}
                    ariaLabel="Daily ledger date"
                  />
                  <GlobalDirectoryExportMenu
                    getExportData={getLedgerExportData}
                    filenameBase={`attendance_ledger_${isoDate}`}
                  />
                </div>
              )}
            />
          )}
          grid={(
            loading ? (
              <div className="att-rpt-skeleton att-rpt-skeleton--table" aria-busy="true" />
            ) : totalRows === 0 ? (
              <div className="att-rpt-empty">
                <User size={28} strokeWidth={1.2} aria-hidden />
                <p>No records found</p>
                <span>Adjust filters or try another period.</span>
              </div>
            ) : (
              <GlobalDataTable
                wrapClassName="att-rpt-table-wrap ub-table-wrap"
                tableClassName="att-rpt-table ub-table"
                tableAriaLabel="Daily attendance ledger"
                headers={tableHeaders}
              >
                {pagedDaily.map((row) => (
                  <TableRow key={row.id}>
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.id}
                        className={["present", "absent", "late", "leave"].includes(col.id) ? "att-rpt-table__flag" : undefined}
                      >
                        {renderLedgerCell(col.id, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </GlobalDataTable>
            )
          )}
          pagination={
            !loading && totalRows > 0 ? (
              <GlobalDirectoryPaginationSection
                page={page}
                totalPages={totalPages}
                total={totalRows}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                label="Attendance ledger pagination"
              />
            ) : null
          }
        />
      </section>
    </div>
  );
}
